import Phaser from 'phaser';
import { Entity, type EntityOptions } from '@/entities/Entity';
import { Hitbox, type CombatSystem, type HitSpec, type Team } from '@/systems/Combat';
import { boxFromCenter } from '@/systems/box';
import {
  initialArmourState,
  applyHitToArmour,
  type JuggernautArmourState,
} from '@/systems/JuggernautArmour';
import {
  initialPoiseState,
  tickLightPoise,
  applyHitToPoise,
  POISE_THRESHOLD_BY_TIER,
  type LightPoiseState,
} from '@/systems/LightPoise';
import {
  tickEnemyAI,
  computeOrbitOffset,
  initialAIState,
  type EnemyAIState,
} from '@/systems/EnemyAI';
import {
  initialPMHallowayState,
  tickPMHallowayAI,
  type PMHallowayAIState,
} from '@/systems/PMHallowayAI';
import { computeSurroundSlot, SURROUND_MIN_ATTACKERS } from '@/systems/GroupTactics';
import type { EnemyGroupManager } from '@/systems/EnemyGroupManager';

/** Hit spec for a basic enemy melee strike. */
const ENEMY_ATTACK_SPEC = { damage: 8, knockback: 30, hitstun: 20 } as const;
/** Frames the enemy's melee hitbox remains active. */
const ATTACK_ACTIVE_FRAMES = 6;
/** Width of the spawned attack hitbox (px). */
const ATTACK_HB_W = 60;
/** Height of the spawned attack hitbox (px). */
const ATTACK_HB_H = 80;

/**
 * Enemy — AI-driven Entity.
 *
 * Behaviour tree (scan → approach → windup → attack → reposition) lives in
 * the pure EnemyAI module; this class is the thin Phaser adapter that applies
 * movement, spawns hitboxes, and wires group-manager pack awareness.
 */
export class Enemy extends Entity {
  readonly team: Team = 'enemy';

  private target?: Entity;

  private aiState: EnemyAIState = initialAIState();

  private pmHallowayState: PMHallowayAIState = initialPMHallowayState();

  /** Index within the enemy group (determines orbit slot). */
  private orbitIndex = 0;

  /** Total enemies in the group (spreads orbit offsets evenly). */
  private orbitTotal = 1;

  /** 0 = easy, 1 = hard. Forwarded to tickEnemyAI. */
  private aggressionScale = 0;

  private groupManager?: EnemyGroupManager;

  private combatSystem?: CombatSystem;

  /** Non-null when this enemy has an armour pool (MY-1886). */
  private armourState: JuggernautArmourState | null = null;

  /** True when this enemy uses the group-tactics behaviour layer (MY-1887). */
  private readonly groupTactics: boolean;

  /** Light-poise tracking (MY-2414). */
  private poiseState: LightPoiseState = initialPoiseState();
  /** Consecutive light hits before shrug-off. */
  private readonly lightPoiseThreshold: number;

  /** True for the MY-1978 cowering PM Halloway coda. */
  private readonly pmHalloway: boolean;

  /** Body fill colour used for the cracked-armour visual once the pool breaks. */
  private static readonly ARMOUR_BROKEN_COLOR = 0x3d1a1a;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: EntityOptions) {
    super(scene, x, y, opts);
    if (opts.armourPool && opts.armourPool > 0) {
      this.armourState = initialArmourState(opts.armourPool);
    }
    this.groupTactics = opts.groupTactics ?? false;
    this.pmHalloway = opts.pmHalloway ?? false;
    this.lightPoiseThreshold = opts.lightPoiseThreshold ?? POISE_THRESHOLD_BY_TIER['galsia'];
  }

  setTarget(target: Entity): void {
    this.target = target;
  }

  /** Provide the combat system so attacks can spawn hitboxes. */
  setCombatSystem(cs: CombatSystem): void {
    this.combatSystem = cs;
  }

  /**
   * Wire up pack-AI group membership.
   * Call after all enemies for a wave are created so orbit offsets spread correctly.
   */
  setGroupInfo(manager: EnemyGroupManager, index: number, total: number): void {
    this.groupManager = manager;
    this.orbitIndex = index;
    this.orbitTotal = total;
  }

  /** Aggression scale: 0 = easy (slow, long cooldowns), 1 = hard (fast, short cooldowns). */
  setAggression(scale: number): void {
    this.aggressionScale = Math.max(0, Math.min(1, scale));
  }

  /**
   * Intercept incoming hits to apply the armour layer before HP is touched.
   *
   * While armour stands: absorbs damage, no HP taken, no hitstun (high poise).
   * On the breaking hit:  armour drops to 0, visual cracks applied, still no HP.
   * Post-break:           delegate to Entity.receiveHit with full original spec.
   */
  override receiveHit(spec: HitSpec, fromCenterX: number): void {
    if (this.isKO) return;

    // Armour layer (juggernaut-style): absorbs all HP/hitstun until broken.
    if (this.armourState) {
      const result = applyHitToArmour(this.armourState, spec);

      if (result.hpDamage > 0) {
        // Armour already broken — fall through to poise check below.
      } else {
        if (result.justBroke) {
          this.onArmourBroken();
        }
        this.flash();
        return;
      }
    }

    // Light-poise check (MY-2414): suppress hitstun after N consecutive light
    // hits; heavy hits / throws / launchers always pass through.
    const suppressHitstun = applyHitToPoise(
      this.poiseState,
      spec.damage,
      this.lightPoiseThreshold,
      false,
      spec.launcher ?? false,
    );

    const effectiveSpec = suppressHitstun ? { ...spec, hitstun: 0 } : spec;
    super.receiveHit(effectiveSpec, fromCenterX);
  }

  private onArmourBroken(): void {
    // Swap placeholder body colour to show cracked/broken armour state
    this.bodyRect.setFillStyle(Enemy.ARMOUR_BROKEN_COLOR);
    if (this.bodyImage) this.bodyImage.setTint(Enemy.ARMOUR_BROKEN_COLOR);
  }

  update(): void {
    this.tickHitstun();
    tickLightPoise(this.poiseState);
    this.tickJump();
    if (this.tickThrown()) return;
    if (this.tickJuggleArc()) return; // mid-juggle — skip AI until landing

    if (!this.target) {
      this.syncAnim({ moving: false });
      return;
    }

    if (this.pmHalloway) {
      this.updatePMHalloway();
      return;
    }

    let orbitOffsetX: number;
    let orbitOffsetY: number;
    if (this.groupTactics && this.orbitTotal >= SURROUND_MIN_ATTACKERS) {
      const slot = computeSurroundSlot(this.orbitIndex, this.orbitTotal);
      orbitOffsetX = slot.offsetX;
      orbitOffsetY = slot.offsetY;
    } else {
      const orbit = computeOrbitOffset(this.orbitIndex, this.orbitTotal);
      orbitOffsetX = orbit.x;
      orbitOffsetY = orbit.y;
    }

    const canAttack = this.groupManager?.canAttack(this.combatId) ?? true;

    const output = tickEnemyAI(
      {
        selfX: this.x,
        selfY: this.y,
        targetX: this.target.x,
        targetY: this.target.y,
        isHitstunned: this.isHitstunned,
        isKO: this.isKO,
        isBeingThrown: this.isBeingThrown,
        canAttack,
        aggressionScale: this.aggressionScale,
        orbitOffsetX,
        orbitOffsetY,
        behaviourFlags: this.groupTactics ? { groupTactics: true } : undefined,
        attackerCount: this.orbitTotal,
        currentHp: this.health.current,
        maxHp: this.health.max,
      },
      this.aiState,
    );

    this.aiState = output.nextState;
    this.setFacing(output.facing);

    if (output.moveX !== 0 || output.moveY !== 0) {
      this.x = Phaser.Math.Clamp(
        this.x + output.moveX * this.speed * 0.6,
        40,
        this.worldWidth - 40,
      );
      this.y += output.moveY * this.speed * 0.6;
    }

    if (output.wantsAttack) {
      this.spawnAttackHitbox();
      this.groupManager?.releaseToken(this.combatId);
    }

    const moving = output.moveX !== 0 || output.moveY !== 0;
    this.syncAnim({ moving, attacking: output.wantsAttack });
  }

  private spawnAttackHitbox(): void {
    if (!this.combatSystem) return;
    const hbX = this.x + this.facing * (this.bodyW / 2 + ATTACK_HB_W / 2);
    const box = boxFromCenter(hbX, this.y, ATTACK_HB_W, ATTACK_HB_H);
    this.combatSystem.spawn(new Hitbox(this, box, { ...ENEMY_ATTACK_SPEC }, ATTACK_ACTIVE_FRAMES));
  }

  private updatePMHalloway(): void {
    if (!this.target) {
      this.syncAnim({ moving: false });
      return;
    }

    const output = tickPMHallowayAI(
      {
        selfX: this.x,
        selfY: this.y,
        targetX: this.target.x,
        targetY: this.target.y,
        isHitstunned: this.isHitstunned,
        isKO: this.isKO,
        isBeingThrown: this.isBeingThrown,
        canAttack: true,
        aggressionScale: this.aggressionScale,
        orbitOffsetX: 0,
        orbitOffsetY: 0,
        currentHp: this.health.current,
        maxHp: this.health.max,
      },
      this.pmHallowayState,
    );

    this.pmHallowayState = output.nextState;
    this.setFacing(output.facing);

    if (output.moveX !== 0 || output.moveY !== 0) {
      this.x = Phaser.Math.Clamp(
        this.x + output.moveX * this.speed * 0.6,
        40,
        this.worldWidth - 40,
      );
      this.y += output.moveY * this.speed * 0.6;
    }

    this.syncAnim({ moving: output.moveX !== 0 || output.moveY !== 0 });
  }
}
